import React, { Fragment, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Menu,
  MenuButton,
  MenuItems,
  MenuItem,
  Transition,
} from '@headlessui/react';
import {
  ChevronDownIcon,
  Squares2X2Icon,
  ListBulletIcon,
  Bars3BottomLeftIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';

import { getMacroName, getMicroName } from '../../utils/getCategoryData';
import { UTILITIES } from '../../utils/utilities';

const splitCsv = (value) =>
  String(value || '')
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);

const SortBar = ({
  displayType,
  setDisplayType,
  selectedSort,
  setSelectedSort,
  searchType,
  isDesktop,
  isAuthenticated,
  hidePastEvents = false,
}) => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();

  const lang = (i18n.language || 'en').split('-')[0];

  const utilityLabel = (id) => {
    const u = UTILITIES.find((x) => x.id === id || x.slug === id);
    return u ? u.labels?.[lang] || u.labels?.en || id : id;
  };

  const applyParams = (next) =>
    navigate(`${location.pathname}?${next.toString()}`);

  const removeKeys = (keys) => {
    const next = new URLSearchParams(location.search);
    keys.forEach((k) => next.delete(k));
    applyParams(next);
  };

  const removeCsvValue = (key, value) => {
    const next = new URLSearchParams(location.search);
    const rest = splitCsv(next.get(key)).filter((x) => x !== String(value));
    if (rest.length) next.set(key, rest.join(','));
    else next.delete(key);
    applyParams(next);
  };

  const clearAllFilters = () => {
    const next = new URLSearchParams();
    next.set('searchType', searchType || 'events');
    applyParams(next);
  };

  const activeFilters = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const chips = [];

    const q = params.get('q');
    if (q) chips.push({ id: 'q', label: `“${q}”`, onRemove: () => removeKeys(['q']) });

    const locationLabel =
      params.get('locationLabel') ||
      params.get('city') ||
      params.get('country');
    if (locationLabel)
      chips.push({
        id: 'loc',
        label: locationLabel,
        onRemove: () =>
          removeKeys([
            'locationLabel',
            'location',
            'city',
            'country',
            'countryCode',
          ]),
      });

    splitCsv(params.get('macroCategories')).forEach((mid) =>
      chips.push({
        id: `macro-${mid}`,
        label: getMacroName(Number(mid)),
        onRemove: () => removeCsvValue('macroCategories', mid),
      })
    );

    splitCsv(params.get('microCategories')).forEach((mid) =>
      chips.push({
        id: `micro-${mid}`,
        label: getMicroName(Number(mid)),
        onRemove: () => removeCsvValue('microCategories', mid),
      })
    );

    if (searchType === 'events') {
      const sd = params.get('startDate');
      const ed = params.get('endDate');
      if (sd || ed)
        chips.push({
          id: 'dates',
          label: [sd, ed].filter(Boolean).join(' – '),
          onRemove: () => removeKeys(['startDate', 'endDate']),
        });

      const mf = params.get('monthFrom');
      const mt = params.get('monthTo');
      if (mf || mt)
        chips.push({
          id: 'months',
          label: [mf, mt].filter(Boolean).join(' – '),
          onRemove: () => removeKeys(['monthFrom', 'monthTo']),
        });

      const maxPrice = params.get('maxPrice');
      if (maxPrice)
        chips.push({
          id: 'price',
          label: `≤ ${maxPrice}€`,
          onRemove: () => removeKeys(['maxPrice']),
        });

      splitCsv(params.get('utilities')).forEach((uid) =>
        chips.push({
          id: `util-${uid}`,
          label: utilityLabel(uid),
          onRemove: () => removeCsvValue('utilities', uid),
        })
      );

      if (params.get('hidePastEvents') === 'false')
        chips.push({
          id: 'show-past',
          label: t('listing.show_past_events'),
          onRemove: () => removeKeys(['hidePastEvents']),
        });

      if (params.get('hideLiveEvents') === 'true')
        chips.push({
          id: 'hide-live',
          label: t('listing.hide_live_events'),
          onRemove: () => removeKeys(['hideLiveEvents']),
        });
    }

    return chips;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, searchType, lang, t]);

  const sortOptions = useMemo(() => {
    if (searchType === 'events') {
      // Sort by event start date only for guests. Authenticated users also get
      // price-based sorts. The unreliable "newest to oldest" (createdAt) sort
      // was removed for both — T2B-355.
      const base = [{ name: t('listing.by_date'), value: 'by-date' }];
      if (!isAuthenticated) return base;
      return [
        ...base,
        { name: t('listing.low_high'), value: 'low-to-high' },
        { name: t('listing.high_low'), value: 'high-to-low' },
      ];
    }
    return [
      { name: t('listing.new_old'), value: 'newest' },
      { name: t('listing.old_new'), value: 'oldest' },
      { name: 'A → Z', value: 'a-z' },
      { name: 'Z → A', value: 'z-a' },
    ];
  }, [t, searchType, isAuthenticated]);

  useEffect(() => {
    if (sortOptions.length === 0) return;
    const values = sortOptions.map((o) => o.value);
    if (!values.includes(selectedSort)) {
      setSelectedSort(sortOptions[0]?.value);
    }
  }, [sortOptions, selectedSort, setSelectedSort]);

  const selectedLabel =
    sortOptions.find((opt) => opt.value === selectedSort)?.name ??
    sortOptions[0]?.name;

  return (
    <div className="flex items-center justify-between gap-3 py-3">
      {/* Left: active filters */}
      <div className="flex flex-wrap items-center gap-2 text-[12.5px] text-gray-500 min-w-0">
        {activeFilters.length > 0 ? (
          <>
            <span className="font-semibold text-ink-strong shrink-0">
              {t('listing.active_filters')}
            </span>
            {activeFilters.map((chip) => (
              <button
                key={chip.id}
                type="button"
                onClick={chip.onRemove}
                title={chip.label}
                className="inline-flex items-center gap-1 max-w-[180px] pl-2.5 pr-1.5 py-0.5 rounded-full bg-paper text-ink-strong text-[11px] font-semibold ring-1 ring-line hover:bg-paper-2 transition"
              >
                <span className="truncate">{chip.label}</span>
                <XMarkIcon className="w-3 h-3 text-gray-400 shrink-0" />
              </button>
            ))}
            <button
              type="button"
              onClick={clearAllFilters}
              className="text-[11px] font-semibold text-gray-400 hover:text-ink-strong underline underline-offset-2 shrink-0"
            >
              {t('listing.clear_all')}
            </button>
          </>
        ) : (
          searchType === 'events' &&
          hidePastEvents && (
            <span className="hidden md:inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-paper text-gray-500 text-[11px] font-semibold ring-1 ring-line">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
              {t('listing.hide_past_events')}
            </span>
          )
        )}
      </div>

      {/* Right: sort + display toggle */}
      <div className="flex items-center gap-2">
        <Menu as="div" className="relative inline-block text-left">
          <MenuButton className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white ring-1 ring-line text-[12px] font-bold text-ink-strong hover:bg-paper-2 transition">
            <Bars3BottomLeftIcon className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">{t('listing.sort')}</span>
            <span>{selectedLabel}</span>
            <ChevronDownIcon className="w-3 h-3 text-gray-400" />
          </MenuButton>
          <Transition
            as={Fragment}
            enter="transition ease-out duration-100"
            enterFrom="transform opacity-0 scale-95"
            enterTo="transform opacity-100 scale-100"
            leave="transition ease-in duration-75"
            leaveFrom="transform opacity-100 scale-100"
            leaveTo="transform opacity-0 scale-95"
          >
            <MenuItems className="absolute right-0 mt-2 w-56 rounded-2xl shadow-lg z-50 bg-white ring-1 ring-line focus:outline-none overflow-hidden">
              <div className="py-1">
                {sortOptions.map((option) => (
                  <MenuItem key={option.value}>
                    {({ active }) => (
                      <button
                        onClick={() => setSelectedSort(option.value)}
                        className={`${
                          active ? 'bg-paper text-ink-strong' : 'text-gray-700'
                        } ${
                          option.value === selectedSort
                            ? 'font-bold text-ink-strong'
                            : ''
                        } flex items-center w-full px-4 py-2 text-[13px] text-left`}
                      >
                        {option.name}
                      </button>
                    )}
                  </MenuItem>
                ))}
              </div>
            </MenuItems>
          </Transition>
        </Menu>

        {/* Display toggle (desktop only) */}
        {isDesktop && (
          <div className="flex items-center gap-0.5 bg-paper rounded-full p-0.5 ring-1 ring-line">
            <button
              type="button"
              aria-label={t('listing.grid_view')}
              onClick={() => setDisplayType('squared')}
              className={`w-8 h-7 grid place-items-center rounded-full transition ${
                displayType === 'squared'
                  ? 'bg-white shadow-sm text-ink-strong'
                  : 'text-gray-400 hover:text-ink-strong'
              }`}
            >
              <Squares2X2Icon className="w-3.5 h-3.5" />
            </button>
            <button
              type="button"
              aria-label={t('listing.list_view')}
              onClick={() => setDisplayType('list')}
              className={`w-8 h-7 grid place-items-center rounded-full transition ${
                displayType === 'list'
                  ? 'bg-white shadow-sm text-ink-strong'
                  : 'text-gray-400 hover:text-ink-strong'
              }`}
            >
              <ListBulletIcon className="w-3.5 h-3.5" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default SortBar;
